import { useMemo } from 'react';
import useSWR from 'swr';

import axios, { fetcher } from 'utils/axios';

export type AnnouncementSeverity = 'info' | 'warning' | 'critical';

export type AnnouncementTargetTenant = {
  id: number;
  name: string;
};

export type AnnouncementItem = {
  id: number;
  title: string;
  message: string;
  severity: AnnouncementSeverity;
  ctaLabel: string | null;
  ctaRoute: string | null;
  active: boolean;
  publishedAt: string | null;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  isGlobal: boolean;
  targetTenantIds: number[];
  targetTenants: AnnouncementTargetTenant[];
  canEdit?: boolean;
};

export type AnnouncementAdminPermissions = {
  canManage: boolean;
  canTargetAll: boolean;
};

export type AnnouncementSummary = {
  total: number;
  active: number;
  critical: number;
  scheduled: number;
};

export type AnnouncementAdminResponse = {
  summary: AnnouncementSummary;
  items: AnnouncementItem[];
  tenants: AnnouncementTargetTenant[];
  permissions: AnnouncementAdminPermissions;
};

export type AnnouncementFeedResponse = {
  generatedAt: string;
  items: AnnouncementItem[];
  unreadCount: number;
};

export type AnnouncementMutationInput = {
  title: string;
  message: string;
  severity: AnnouncementSeverity;
  ctaLabel: string | null;
  ctaRoute: string | null;
  active: boolean;
  publishedAt: string | null;
  startsAt: string | null;
  endsAt: string | null;
  targetTenantIds: number[];
};

type AdminEnvelope = { item: AnnouncementAdminResponse };
type FeedEnvelope = { item: AnnouncementFeedResponse };
type ItemEnvelope = { item: AnnouncementItem };

const adminEndpoint = '/announcements';
const feedEndpoint = '/announcements/feed';

export function useGetAnnouncementsAdmin() {
  const { data, isLoading, error, isValidating, mutate } = useSWR<AdminEnvelope>(adminEndpoint, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    refreshInterval: 30000,
    refreshWhenHidden: false,
    refreshWhenOffline: false
  });

  return useMemo(
    () => ({
      announcementsAdmin: data?.item ?? null,
      announcementsAdminLoading: isLoading,
      announcementsAdminError: error,
      announcementsAdminValidating: isValidating,
      mutateAnnouncementsAdmin: mutate
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

export function useGetAnnouncementFeed() {
  const { data, isLoading, error, isValidating, mutate } = useSWR<FeedEnvelope>(feedEndpoint, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    refreshInterval: 60000,
    refreshWhenHidden: false,
    refreshWhenOffline: false
  });

  return useMemo(
    () => ({
      announcementFeed: data?.item ?? null,
      announcementFeedLoading: isLoading,
      announcementFeedError: error,
      announcementFeedValidating: isValidating,
      mutateAnnouncementFeed: mutate
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

export async function createAnnouncement(input: AnnouncementMutationInput): Promise<AnnouncementItem> {
  const response = await axios.post<ItemEnvelope>(adminEndpoint, input);
  return response.data.item;
}

export async function updateAnnouncement(id: number, input: AnnouncementMutationInput): Promise<AnnouncementItem> {
  const response = await axios.put<ItemEnvelope>(`${adminEndpoint}/${id}`, input);
  return response.data.item;
}
